import {
  Card,
  Group,
  TextInput,
  Button,
  Select,
  ActionIcon,
  Text,
} from '@mantine/core';
import { Search, Grid, List } from 'react-feather';
import classes from './CoachesFilters.module.css';

type ViewMode = 'grid' | 'list';
type StatusFilter = string;

// Status filter pill options (MED-683: derived Active/Inactive only)
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Sort options for dropdown
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'location', label: 'Location' },
  { value: 'timezone', label: 'Timezone' },
  { value: 'cohorts', label: 'Number of Cohorts' },
  { value: 'satisfaction', label: 'Satisfaction Rating' },
];

export interface CoachesFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedStatusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  sortBy: string;
  onSortByChange: (value: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

export function CoachesFilters({
  searchValue,
  onSearchChange,
  selectedStatusFilter,
  onStatusFilterChange,
  sortBy,
  onSortByChange,
  viewMode,
  onViewModeChange,
}: CoachesFiltersProps) {
  return (
    <Card withBorder p="md" mb="lg" className={classes.filterPanel} styles={{ root: { boxShadow: 'none' } }}>
      <Group justify="space-between" wrap="nowrap">
        {/* Left: Search and Filter Pills */}
        <Group gap="md" wrap="nowrap">
          <TextInput
            placeholder="Search coaches..."
            leftSection={<Search size={16} />}
            value={searchValue}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            className={classes.searchInput}
          />

          {/* Status Filter Pills */}
          <Group gap={4}>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={selectedStatusFilter === option.value ? 'filled' : 'outline'}
                color={selectedStatusFilter === option.value ? 'medleyGreen' : 'gray'}
                size="xs"
                className={classes.filterPill}
                onClick={() => onStatusFilterChange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </Group>

        </Group>

        {/* Right: Sort and View Toggle */}
        <Group gap="md" wrap="nowrap">
          <Select
            placeholder="Sort by"
            data={SORT_OPTIONS}
            value={sortBy}
            onChange={(value) => onSortByChange(value || 'name')}
            leftSection={<Text size="xs" c="dimmed">Sort by:</Text>}
            leftSectionWidth={60}
            w={200}
            size="sm"
            styles={{
              input: { paddingLeft: 60 },
            }}
          />

          {/* View Mode Toggle */}
          <Group gap={0} className={classes.viewToggle}>
            <ActionIcon
              variant={viewMode === 'grid' ? 'filled' : 'subtle'}
              color={viewMode === 'grid' ? 'medleyGreen' : 'gray'}
              size="md"
              onClick={() => onViewModeChange('grid')}
              className={classes.viewToggleButton}
            >
              <Grid size={16} />
            </ActionIcon>
            <ActionIcon
              variant={viewMode === 'list' ? 'filled' : 'subtle'}
              color={viewMode === 'list' ? 'medleyGreen' : 'gray'}
              size="md"
              onClick={() => onViewModeChange('list')}
              className={classes.viewToggleButton}
            >
              <List size={16} />
            </ActionIcon>
          </Group>
        </Group>
      </Group>
    </Card>
  );
}
